import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, PenLine, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  sectorDrawingsService,
  SectorDrawingError,
  type SectorDrawings,
} from '../../../services/sector-drawings';
import type { CragRouteSector } from '../../../types/crag-route';

const POLL_MS = 3000;

/** What the panel has words for. The API's own codes and a failed run's share them. */
const ERROR_KEYS: Record<string, string> = {
  drawing_budget_exceeded: 'budget_exceeded',
  budget_exceeded: 'budget_exceeded',
  drawing_not_configured: 'not_configured',
  not_configured: 'not_configured',
  drawing_in_progress: 'in_progress',
  drawing_not_applicable: 'not_applicable',
  refused: 'refused',
  provider_busy: 'provider_busy',
  provider_error: 'provider_error',
  timed_out: 'timed_out',
};

function errorKey(code: string | null | undefined) {
  return `cragAdmin.routes.sectorDrawing.errors.${ERROR_KEYS[code ?? ''] ?? 'generic'}`;
}

interface SectorDrawingCardProps {
  sector: CragRouteSector;
  crag: string;
  /** A photo picked but not saved yet: the drawing would be made from the old one. */
  disabled: boolean;
  /** The sector's picture changed, so the page has to read it again. */
  onChanged: () => Promise<unknown>;
}

/**
 * Makes an ink drawing out of the sector's photo and, once somebody has looked at it,
 * puts it in the photo's place. The routes' lines stay where they are, since the drawing
 * has the photo's framing, and the photo can always be taken back.
 *
 * Nothing is generated by opening this: only the button is a paid call.
 */
export default function SectorDrawingCard({ sector, crag, disabled, onChanged }: SectorDrawingCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SectorDrawings | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState(await sectorDrawingsService.list(crag, sector.id));
  }, [crag, sector.id]);

  useEffect(() => {
    if (!open) return;
    setErrorText(null);
    load().catch(() => setErrorText(errorKey(null)));
  }, [open, load]);

  const running = !!state?.drawings.some((drawing) => drawing.status === 'queued' || drawing.status === 'running');

  useEffect(() => {
    if (!open || !running) return;
    const timer = window.setInterval(() => load().catch(() => undefined), POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, running, load]);

  const ready = state?.drawings.filter((drawing) => drawing.status === 'ready' && drawing.image_url) ?? [];
  const selected = ready.find((drawing) => drawing.id === selectedId) ?? ready[0] ?? null;
  // Only the latest run's failure is news: an older one was followed by another try.
  const latest = state?.drawings[0];
  const failure = latest?.status === 'failed' ? errorKey(latest.error_code) : null;
  const shownError = errorText ?? failure;

  const act = async (action: () => Promise<SectorDrawings>, onDone?: (next: SectorDrawings) => Promise<void>) => {
    setBusy(true);
    setErrorText(null);
    try {
      const next = await action();
      setState(next);
      await onDone?.(next);
    } catch (error) {
      setErrorText(errorKey(error instanceof SectorDrawingError ? error.code : null));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    act(() => sectorDrawingsService.generate(crag, sector.id), async () => setSelectedId(null));

  const apply = () => {
    if (!selected) return;
    void act(() => sectorDrawingsService.apply(crag, sector.id, selected.id), async () => {
      // Closed first: the page goes on to show the new picture with its lines on it,
      // which is the thing to look at now, and it unmounts this while measuring it.
      setOpen(false);
      await onChanged();
      toast({ title: t('cragAdmin.routes.sectorDrawing.applied') });
    });
  };

  const restore = () =>
    act(() => sectorDrawingsService.restore(crag, sector.id), async () => {
      setOpen(false);
      await onChanged();
      toast({ title: t('cragAdmin.routes.sectorDrawing.restored') });
    });

  const photoUrl = state?.sourceUrl ?? sector.image_url;

  return (
    <section className="space-y-2" aria-label={t('cragAdmin.routes.sectorDrawing.title')}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={disabled || !sector.image_url}
        onClick={() => setOpen(true)}
      >
        <PenLine className="size-4" aria-hidden />
        {t('cragAdmin.routes.sectorDrawing.open')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('cragAdmin.routes.sectorDrawing.title')}</DialogTitle>
            <DialogDescription>{t('cragAdmin.routes.sectorDrawing.hint')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <figure className="space-y-1">
              <figcaption className="text-xs text-muted-foreground">{t('cragAdmin.routes.sectorDrawing.photo')}</figcaption>
              {photoUrl && <img src={photoUrl} alt="" className="w-full rounded border" />}
            </figure>
            <figure className="space-y-1">
              <figcaption className="text-xs text-muted-foreground">{t('cragAdmin.routes.sectorDrawing.drawing')}</figcaption>
              {selected ? (
                <img src={selected.image_url ?? undefined} alt="" className="w-full rounded border" />
              ) : (
                <div className="flex aspect-video items-center justify-center rounded border border-dashed p-4 text-center text-sm text-muted-foreground">
                  {running ? t('cragAdmin.routes.sectorDrawing.running') : t('cragAdmin.routes.sectorDrawing.empty')}
                </div>
              )}
            </figure>
          </div>

          {ready.length > 1 && (
            <div className="flex flex-wrap gap-2" role="group" aria-label={t('cragAdmin.routes.sectorDrawing.candidates')}>
              {ready.map((drawing, index) => (
                <button
                  key={drawing.id}
                  type="button"
                  aria-pressed={drawing.id === selected?.id}
                  aria-label={t('cragAdmin.routes.sectorDrawing.candidate', { number: index + 1 })}
                  onClick={() => setSelectedId(drawing.id)}
                  className={cn('overflow-hidden rounded border-2', drawing.id === selected?.id ? 'border-primary' : 'border-transparent')}
                >
                  <img src={drawing.image_url ?? undefined} alt="" className="h-14 w-auto" />
                </button>
              ))}
            </div>
          )}

          {running && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t('cragAdmin.routes.sectorDrawing.running')}
            </p>
          )}
          {shownError && <p className="text-sm text-destructive">{t(shownError)}</p>}

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {state?.canRestore && (
                <Button type="button" variant="ghost" className="gap-2" disabled={busy} onClick={() => void restore()}>
                  <RotateCcw className="size-4" aria-hidden />
                  {t('cragAdmin.routes.sectorDrawing.restore')}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant={selected ? 'outline' : 'default'}
                disabled={busy || running || !state}
                onClick={() => void generate()}
              >
                {t(ready.length ? 'cragAdmin.routes.sectorDrawing.generateAnother' : 'cragAdmin.routes.sectorDrawing.generate')}
              </Button>
              {selected && !selected.applied && (
                <Button type="button" disabled={busy} onClick={apply}>
                  {t('cragAdmin.routes.sectorDrawing.apply')}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
